/**
 * NaamDarbar — Auth Context (Firebase, LAZY)
 *
 * Provides:
 *  - Firebase auth state (user, loading)
 *  - App-level profile (role, claims) from the auth-service
 *  - Sign in/out helpers
 *  - getIdToken() for API calls
 *
 * Firebase JS SDK is lazy-loaded via `lib/firebase.ts::getFirebaseAuth()`:
 *  - Anonymous first-time visitors NEVER load it (saves ~33 kB gzipped on
 *    initial page weight + a TLS round-trip to firebaseinstallations).
 *  - Returning signed-in users (localStorage `kd:was-authed` flag set) trigger
 *    the load on mount so onAuthStateChanged fires as before.
 *  - Anonymous users who click "Sign In" trigger the load on demand.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { User } from 'firebase/auth';
import {
  getFirebaseAuth,
  wasAuthed,
  markAuthed,
  clearAuthed,
} from '../lib/firebase';

// ── Types ────────────────────────────────────────────────────────────

export interface AppProfile {
  id: string;
  firebase_uid: string;
  email: string | null;
  display_name: string | null;
  photo_url: string | null;
  role: 'user' | 'raagi' | 'admin';
  intended_role: 'user' | 'raagi' | null;
  is_active: boolean;
  email_verified: boolean;
  phone: string | null;
  phone_verified: boolean;
  created_at: string;
  updated_at: string;
}

export interface ActiveClaim {
  id: string;
  artist_id: string;
  /** Name of the artist/profile being claimed — shown across the claim flow. */
  artist_name?: string | null;
  /** v2 status vocabulary — 'draft' = phone not yet verified, invisible to admins */
  status: 'draft' | 'submitted' | 'under_review' | 'needs_more_info' | 'approved' | 'rejected';
  created_at: string;
}

interface AuthContextValue {
  /** Firebase user (null if not signed in) */
  firebaseUser: User | null;
  /** App-level profile from auth-service (null if not registered) */
  profile: AppProfile | null;
  /** Active ragi claim (if any) */
  activeClaim: ActiveClaim | null;
  /** True if the user has ever created any claim (any status). Used to gate the raagi onboarding. */
  hasInitiatedClaim: boolean;
  /** True while Firebase is checking initial auth state OR the backend profile is being fetched */
  loading: boolean;
  /** Unified flag — true during Firebase init OR backend profile fetch. Use this for loading screens. */
  isLoadingAuth: boolean;
  /** True if Firebase user exists AND app profile is loaded */
  isAuthenticated: boolean;
  isRagi: boolean;
  isAdmin: boolean;

  /** Get a fresh Firebase ID token for API calls */
  getIdToken: () => Promise<string>;

  /** Sign in with Google popup */
  signInWithGoogle: () => Promise<void>;
  /** Send a WhatsApp OTP to start phone sign-in / sign-up */
  sendPhoneOtp: (phone: string) => Promise<void>;
  /** Verify the WhatsApp OTP and complete sign-in via a Firebase custom token */
  signInWithPhoneOtp: (phone: string, code: string) => Promise<void>;
  /** Sign in with email/password */
  signInWithEmail: (email: string, password: string) => Promise<void>;
  /** Create account with email/password */
  signUpWithEmail: (email: string, password: string, displayName: string) => Promise<void>;
  /** Send a password-reset email via Firebase */
  sendPasswordReset: (email: string) => Promise<void>;
  /** Sign out */
  signOut: () => Promise<void>;

  /** Refresh the app profile from the backend */
  refreshProfile: () => Promise<void>;
  /** Persist the user's self-declared role (user | raagi) and optionally their display name.
   *  Throws if the backend rejects it (e.g. a verified Raagi trying to switch role → 409). */
  setIntendedRole: (role: 'user' | 'raagi', displayName?: string) => Promise<void>;
  /** Update the user's display name (PATCH /auth/me). Throws on failure. */
  updateDisplayName: (displayName: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

// ── Auth service URL ─────────────────────────────────────────────────

// Auth endpoints live on the backend. Prefer VITE_AUTH_SERVICE_URL if set;
// otherwise derive from VITE_API_URL (which is always set in production).
// Fallback to '' only in local dev where both point at the same origin.
const AUTH_SERVICE_URL =
  import.meta.env.VITE_AUTH_SERVICE_URL ||
  (import.meta.env.VITE_API_URL ? `${import.meta.env.VITE_API_URL}/api/v1` : '');

// ── Profile cache helpers ────────────────────────────────────────────

const PROFILE_CACHE_KEY = 'rd_profile_cache';
const CLAIM_CACHE_KEY = 'rd_claim_cache';
const HAS_INITIATED_CLAIM_KEY = 'rd_has_initiated_claim';

// Use localStorage (not sessionStorage) so the cached profile survives a page
// reload. Mobile browsers frequently discard sessionStorage between loads,
// which left `profile === null` on every reload and forced the "Signing in…"
// screen to show — even though the `kd:was-authed` flag (localStorage) said the
// user was already signed in. localStorage matches that flag's lifetime so
// mobile gets the same instant render as desktop.
function getCachedProfile(): AppProfile | null {
  try {
    const raw = localStorage.getItem(PROFILE_CACHE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

function getCachedClaim(): ActiveClaim | null {
  try {
    const raw = localStorage.getItem(CLAIM_CACHE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

function getCachedHasInitiatedClaim(): boolean {
  try {
    return localStorage.getItem(HAS_INITIATED_CLAIM_KEY) === 'true';
  } catch { return false; }
}

function setCachedProfile(profile: AppProfile | null) {
  try {
    if (profile) localStorage.setItem(PROFILE_CACHE_KEY, JSON.stringify(profile));
    else localStorage.removeItem(PROFILE_CACHE_KEY);
  } catch { /* ignore */ }
}

function setCachedClaim(claim: ActiveClaim | null) {
  try {
    if (claim) localStorage.setItem(CLAIM_CACHE_KEY, JSON.stringify(claim));
    else localStorage.removeItem(CLAIM_CACHE_KEY);
  } catch { /* ignore */ }
}

function setCachedHasInitiatedClaim(v: boolean) {
  try {
    if (v) localStorage.setItem(HAS_INITIATED_CLAIM_KEY, 'true');
    else localStorage.removeItem(HAS_INITIATED_CLAIM_KEY);
  } catch { /* ignore */ }
}

// ── Provider ─────────────────────────────────────────────────────────

export function AuthProvider({ children }: { children: ReactNode }) {
  const [firebaseUser, setFirebaseUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<AppProfile | null>(getCachedProfile);
  const [activeClaim, setActiveClaim] = useState<ActiveClaim | null>(getCachedClaim);
  const [hasInitiatedClaim, setHasInitiatedClaim] = useState<boolean>(getCachedHasInitiatedClaim);
  // Anonymous-first-visit fast path: if the user has never signed in, we know
  // there's no Firebase session to wait for → render with loading=false
  // immediately, never importing the SDK.
  const [loading, setLoading] = useState<boolean>(() => wasAuthed());
  // True while _registerAndFetchProfile is in-flight (covers new sign-ins where
  // loading starts false but the backend call still races the navigation).
  const [profileLoading, setProfileLoading] = useState(false);

  // ── Firebase auth state listener (lazy) ───────────────────────────
  // Only initialise Firebase if a previous session was authenticated. The
  // anonymous path bails out before paying the network/CPU cost.
  useEffect(() => {
    if (!wasAuthed()) return;

    let unsubscribe: (() => void) | null = null;
    let cancelled = false;

    (async () => {
      try {
        const { auth, mod } = await getFirebaseAuth();
        if (cancelled) return;
        unsubscribe = mod.onAuthStateChanged(auth, async (user) => {
          setFirebaseUser(user);
          if (user) {
            await _registerAndFetchProfile(user);
          } else {
            // Token expired or signed out elsewhere → clear local state
            setProfile(null);
            setActiveClaim(null);
            setCachedProfile(null);
            setCachedClaim(null);
            clearAuthed();
          }
          setLoading(false);
        });
      } catch (err) {
        console.error('[Auth] Firebase init failed:', err);
        setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      if (unsubscribe) unsubscribe();
    };
  }, []);

  // ── Register with backend & fetch profile ────────────────────────
  const _registerAndFetchProfile = async (user: User) => {
    setProfileLoading(true);
    try {
      const token = await user.getIdToken();

      // POST /auth/register only on first login (no cached profile yet).
      // Returning users are already registered — skipping this halves the
      // number of sequential backend round-trips on every session load.
      if (!getCachedProfile()) {
        await fetch(`${AUTH_SERVICE_URL}/auth/register`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ display_name: user.displayName }),
        });
      }

      // GET /auth/me
      const meResp = await fetch(`${AUTH_SERVICE_URL}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (meResp.ok) {
        const data = await meResp.json();
        setProfile(data.profile);
        setActiveClaim(data.active_claim ?? null);
        setHasInitiatedClaim(data.has_initiated_claim ?? false);
        setCachedProfile(data.profile);
        setCachedClaim(data.active_claim ?? null);
        setCachedHasInitiatedClaim(data.has_initiated_claim ?? false);
      }
    } catch (err) {
      console.error('[Auth] Failed to register/fetch profile:', err);
    } finally {
      setProfileLoading(false);
    }
  };

  // ── Public API ────────────────────────────────────────────────────

  const getIdToken = useCallback(async (): Promise<string> => {
    if (!firebaseUser) throw new Error('Not authenticated');
    return firebaseUser.getIdToken();
  }, [firebaseUser]);

  const signInWithGoogle = useCallback(async () => {
    const { auth, googleProvider, mod } = await getFirebaseAuth();
    const cred = await mod.signInWithPopup(auth, googleProvider);
    markAuthed();
    // Directly complete the sign-in here rather than relying on onAuthStateChanged.
    // The listener is only subscribed when wasAuthed() was true on mount — on a
    // first-ever sign-in it was false, so the effect bailed early and there is
    // no listener. Setting firebaseUser + fetching the profile here fixes the
    // "need to reload after Google sign-in" bug.
    setFirebaseUser(cred.user);
    await _registerAndFetchProfile(cred.user);
  }, []);

  const sendPhoneOtp = useCallback(async (phone: string) => {
    const resp = await fetch(`${AUTH_SERVICE_URL}/auth/phone-login/send`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ phone }),
    });
    if (!resp.ok) {
      const detail = await resp.json().catch(() => null);
      throw new Error(detail?.detail || 'Could not send verification code. Please try again.');
    }
  }, []);

  const signInWithPhoneOtp = useCallback(async (phone: string, code: string) => {
    // 1. Verify the OTP server-side → Firebase custom token.
    const resp = await fetch(`${AUTH_SERVICE_URL}/auth/phone-login/verify`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ phone, code }),
    });
    if (!resp.ok) {
      const detail = await resp.json().catch(() => null);
      throw new Error(detail?.detail || 'Invalid or expired code.');
    }
    const { firebase_custom_token } = await resp.json();

    // 2. Exchange the custom token for a real Firebase session. From here the
    // flow matches every other sign-in: the ID token carries phone_number, so
    // the backend identity resolver creates/links the profile on /auth/me.
    const { auth, mod } = await getFirebaseAuth();
    const cred = await mod.signInWithCustomToken(auth, firebase_custom_token);
    markAuthed();
    setFirebaseUser(cred.user);
    await _registerAndFetchProfile(cred.user);
  }, []);

  const signInWithEmail = useCallback(async (email: string, password: string) => {
    const { auth, mod } = await getFirebaseAuth();
    const cred = await mod.signInWithEmailAndPassword(auth, email, password);
    markAuthed();
    setFirebaseUser(cred.user);
    await _registerAndFetchProfile(cred.user);
  }, []);

  const signUpWithEmail = useCallback(
    async (email: string, password: string, displayName: string) => {
      const { auth, mod } = await getFirebaseAuth();
      const cred = await mod.createUserWithEmailAndPassword(auth, email, password);
      await mod.updateProfile(cred.user, { displayName });
      markAuthed();
      setFirebaseUser(cred.user);
      // Don't call _registerAndFetchProfile here — the caller (StepAccount) will
      // do it via setIntendedRole or the auto-subscribe on next mount once
      // wasAuthed() becomes true. The profile is loaded lazily so the wizard
      // can continue without waiting for the full backend round-trip.
    },
    [],
  );

  const sendPasswordReset = useCallback(async (email: string) => {
    const { auth, mod } = await getFirebaseAuth();
    await mod.sendPasswordResetEmail(auth, email);
  }, []);

  const signOutFn = useCallback(async () => {
    const { auth, mod } = await getFirebaseAuth();
    await mod.signOut(auth);
    setProfile(null);
    setActiveClaim(null);
    setHasInitiatedClaim(false);
    setCachedProfile(null);
    setCachedClaim(null);
    setCachedHasInitiatedClaim(false);
    clearAuthed();
  }, []);

  const refreshProfile = useCallback(async () => {
    if (firebaseUser) {
      await _registerAndFetchProfile(firebaseUser);
    }
  }, [firebaseUser]);

  const setIntendedRole = useCallback(async (role: 'user' | 'raagi', displayName?: string) => {
    // Access Firebase's current user directly — avoids depending on React
    // state timing (firebaseUser may not be set yet right after sign-up).
    const { auth } = await getFirebaseAuth();
    const currentUser = auth.currentUser;
    if (!currentUser) return;
    const token = await currentUser.getIdToken();
    // Persist role + (optionally) the name in a single PATCH. Phone signups
    // collect the name on the role-selection screen since WhatsApp never exposes
    // it; email/Google users already have one and omit it.
    const body: { intended_role: 'user' | 'raagi'; display_name?: string } = { intended_role: role };
    const name = displayName?.trim();
    if (name) body.display_name = name;
    const resp = await fetch(`${AUTH_SERVICE_URL}/auth/me`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(body),
    });
    if (!resp.ok) {
      const detail = await resp.json().catch(() => null);
      throw new Error(detail?.detail || 'Could not update your role.');
    }
    // Refresh so profile.intended_role is current
    await _registerAndFetchProfile(currentUser);
  }, []);

  const updateDisplayName = useCallback(async (displayName: string) => {
    const name = displayName.trim();
    if (!name) throw new Error('Name cannot be empty.');
    const { auth } = await getFirebaseAuth();
    const currentUser = auth.currentUser;
    if (!currentUser) throw new Error('Not signed in.');
    const token = await currentUser.getIdToken();
    const resp = await fetch(`${AUTH_SERVICE_URL}/auth/me`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ display_name: name }),
    });
    if (!resp.ok) {
      const detail = await resp.json().catch(() => null);
      throw new Error(detail?.detail || 'Could not update your name.');
    }
    await _registerAndFetchProfile(currentUser);
  }, []);

  // ── Derived state ─────────────────────────────────────────────────
  const isAuthenticated = !!firebaseUser;
  // Expose a single loading flag: true during initial Firebase check (returning
  // users) OR while the backend profile fetch is in-flight (new sign-ins).
  const isLoadingAuth = loading || profileLoading;
  const isRagi = profile?.role === 'raagi' || profile?.role === 'admin';
  const isAdmin = profile?.role === 'admin';

  const value = useMemo<AuthContextValue>(
    () => ({
      firebaseUser,
      profile,
      activeClaim,
      hasInitiatedClaim,
      loading,
      isLoadingAuth,
      isAuthenticated,
      isRagi,
      isAdmin,
      getIdToken,
      signInWithGoogle,
      sendPhoneOtp,
      signInWithPhoneOtp,
      signInWithEmail,
      signUpWithEmail,
      sendPasswordReset,
      signOut: signOutFn,
      refreshProfile,
      setIntendedRole,
      updateDisplayName,
    }),
    [
      firebaseUser,
      profile,
      activeClaim,
      hasInitiatedClaim,
      loading,
      isLoadingAuth,
      isAuthenticated,
      isRagi,
      isAdmin,
      getIdToken,
      signInWithGoogle,
      sendPhoneOtp,
      signInWithPhoneOtp,
      signInWithEmail,
      signUpWithEmail,
      sendPasswordReset,
      signOutFn,
      refreshProfile,
      setIntendedRole,
      updateDisplayName,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// ── Hook ─────────────────────────────────────────────────────────────

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
